import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react'
import {createPortal} from 'react-dom'
import {useHistory, useLocation} from 'react-router-dom'
import {usePageData} from '../../core'
import {singleProjectLandingRoute} from '../../../../app/modules/projectdetails/selectionMode'
import ProjectSelectionTree, {ProjectTreeOption} from '../../../../app/shared/components/ProjectSelectionTree'
import {useProjectSelection} from '../../../../app/modules/projectdetails/hooks/useProjectSelection'
import {useProjectSelectionOptions} from '../../../../app/modules/projectsetup/hooks/useProjectSelectionOptions'
import {
  createProjectSelectionDetail, dispatchProjectSelectionChange, INCLUDE_ARCHIVED_PROJECTS_STORAGE_KEY,
  PROJECT_SELECTION_NAVIGATION_STATE_KEY, ProjectSelectionProject, readProjectSelectionFromNavigationState,
  readStoredProjectSelection, resolveProjectSelection, SELECTED_PROJECTS_STORAGE_KEY,
} from '../../../../app/modules/projectdetails/constants'

const ProjectSelector: React.FC = () => {
  const history = useHistory()
  const location = useLocation()
  const {currentUser: {activePages}} = usePageData()
  const {projects: selected} = useProjectSelection()
  const [includeArchived, setIncludeArchived] = useState(() =>
    localStorage.getItem(INCLUDE_ARCHIVED_PROJECTS_STORAGE_KEY) === 'true' || readStoredProjectSelection().some((project) => project.isArchived))
  const {projects, loading, error, refresh} = useProjectSelectionOptions(includeArchived)
  const [open, setOpen] = useState(false)
  const [position, setPosition] = useState({left: 8, top: 8, width: 480, height: 480})
  const trigger = useRef<HTMLButtonElement>(null)
  const controls = useRef<HTMLDivElement>(null)
  const popup = useRef<HTMLDivElement>(null)
  const appliedNavigation = useRef<unknown>()

  const navigate = useCallback((next: ProjectSelectionProject[], replace = false) => {
    dispatchProjectSelectionChange(createProjectSelectionDetail(next))
    const state = {[PROJECT_SELECTION_NAVIGATION_STATE_KEY]: createProjectSelectionDetail(readStoredProjectSelection())}
    const route = next.length > 1 ? '/portfolio-overview' : singleProjectLandingRoute(activePages.map((page) => page.name))
    if (replace) history.replace(route, state)
    else history.push(route, state)
  }, [history, activePages])

  useEffect(() => {
    if (appliedNavigation.current === location.state) return
    const detail = readProjectSelectionFromNavigationState(location.state)
    appliedNavigation.current = location.state
    if (!detail) return
    const next = resolveProjectSelection(detail)
    if (next.some((project) => project.isArchived)) setIncludeArchived(true)
    if (JSON.stringify(next) !== JSON.stringify(readStoredProjectSelection())) {
      dispatchProjectSelectionChange(createProjectSelectionDetail(next))
    }
  }, [location.state])

  useEffect(() => {
    if (loading || error) return
    const current = readStoredProjectSelection()
    if (current.some((project) => project.isArchived) && !includeArchived) {
      setIncludeArchived(true)
      return
    }
    const hasMultiSelectionStorage = localStorage.getItem(SELECTED_PROJECTS_STORAGE_KEY) !== null
    const hadSelection = hasMultiSelectionStorage || current.length > 0
    const next = hadSelection
      ? current.map((project) => projects.find((option) => option.id === project.id)).filter((project): project is typeof projects[number] => Boolean(project))
      : projects.slice(0, 1)
    const snapshots = next.map((project): ProjectSelectionProject => ({
      id: project.id, projectName: project.projectName, projectNumber: project.projectNumber,
      programmeName: project.programmeName, isArchived: Boolean(project.isArchived), isMock: false,
    }))
    // Compare canonical snapshots so metadata refreshes cannot cause selection loops.
    const canonical = resolveProjectSelection(createProjectSelectionDetail(snapshots))
    if (!hasMultiSelectionStorage || JSON.stringify(canonical) !== JSON.stringify(current)) {
      const idsChanged = current.map((project) => project.id).join(',') !== canonical.map((project) => project.id).join(',')
      if (hadSelection && idsChanged) navigate(canonical, true)
      else dispatchProjectSelectionChange(createProjectSelectionDetail(canonical))
    }
  }, [projects, loading, error, includeArchived, navigate, selected])

  useEffect(() => {
    localStorage.setItem(INCLUDE_ARCHIVED_PROJECTS_STORAGE_KEY, String(includeArchived))
  }, [includeArchived])

  useEffect(() => {
    if (!open) return
    let frame = 0
    const updatePosition = () => {
      const rect = trigger.current?.getBoundingClientRect()
      if (!rect) return
      const width = Math.min(480, window.innerWidth - 16)
      const below = window.innerHeight - rect.bottom - 16
      const height = Math.min(500, Math.max(below, rect.top - 16))
      const top = below >= 240 || below >= rect.top - 16 ? rect.bottom + 8 : Math.max(8, rect.top - height - 8)
      setPosition({left: Math.max(8, Math.min(rect.left, window.innerWidth - width - 8)), top, width, height: Math.min(height, window.innerHeight - top - 8)})
    }
    const schedulePosition = () => {
      cancelAnimationFrame(frame)
      frame = requestAnimationFrame(updatePosition)
    }
    const outside = (event: Event) => {
      if (!popup.current?.contains(event.target as Node) && !controls.current?.contains(event.target as Node)) setOpen(false)
    }
    const escape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()
        setOpen(false)
        trigger.current?.focus()
      }
    }
    updatePosition()
    popup.current?.querySelector<HTMLInputElement>('input[type="search"]')?.focus()
    document.addEventListener('mousedown', outside)
    document.addEventListener('focusin', outside)
    document.addEventListener('keydown', escape)
    window.addEventListener('resize', schedulePosition)
    window.addEventListener('scroll', schedulePosition, true)
    return () => {
      cancelAnimationFrame(frame)
      document.removeEventListener('mousedown', outside)
      document.removeEventListener('focusin', outside)
      document.removeEventListener('keydown', escape)
      window.removeEventListener('resize', schedulePosition)
      window.removeEventListener('scroll', schedulePosition, true)
    }
  }, [open])

  const options = useMemo<ProjectTreeOption[]>(() => projects.filter((project) => includeArchived || !project.isArchived).map((project) => ({
    value: String(project.id), label: project.projectName, projectNumber: project.projectNumber,
    programmeName: project.programmeName, isArchived: project.isArchived,
  })), [projects, includeArchived])
  const selectedOptions = useMemo<ProjectTreeOption[]>(() => selected.map((project) => ({
    value: String(project.id), label: project.projectName, projectNumber: project.projectNumber,
    programmeName: project.programmeName, isArchived: project.isArchived,
  })), [selected])
  const selectProjects = (next: ProjectTreeOption[]) => navigate(next.map((project) => ({
    id: Number(project.value), projectName: project.label, projectNumber: project.projectNumber ?? '',
    programmeName: project.programmeName, isArchived: project.isArchived, isMock: false,
  })))

  return (
    <div ref={controls}>
      <div className='d-flex align-items-center justify-content-between gap-2 flex-wrap mb-2'>
        <label htmlFor='aside-project-selector' id='aside-project-selector-label' className='text-muted text-uppercase fs-8 fw-semibold mb-0'>Projects</label>
        <label className='form-check form-switch form-check-custom gap-2 mb-0'>
          <input type='checkbox' className='form-check-input' checked={includeArchived}
            onChange={(event) => {
              const include = event.target.checked
              if (!include && selected.some((project) => project.isArchived)) navigate(selected.filter((project) => !project.isArchived))
              setIncludeArchived(include)
            }} />
          <span className='form-check-label ms-0 fs-8'>Include Archive</span>
        </label>
      </div>
      <button id='aside-project-selector' ref={trigger} type='button'
        className='btn btn-light border w-100 d-flex align-items-center justify-content-between gap-2 text-start px-3'
        style={{height: '4.75rem'}} aria-haspopup='dialog' aria-expanded={open} aria-controls='aside-project-tree'
        onClick={() => setOpen((previous) => !previous)}>
        <span className='d-block flex-grow-1' style={{minWidth: 0}}>
          <span className='d-block text-truncate fw-semibold text-gray-900' title={selected.length === 1 ? selected[0].projectName : undefined}>
            {selected.length > 1 ? `${selected.length} projects selected` : selected[0]?.projectName ?? (loading ? 'Loading projects...' : 'Select projects')}
          </span>
          <span className='d-flex align-items-center gap-2 fs-8 text-muted'>
            <span className='text-truncate'>{selected.length === 1 ? selected[0].projectNumber : 'Portfolio selection'}</span>
            {selected.length === 1 && selected[0].isArchived && <span className='badge badge-light-secondary'>Archived</span>}
          </span>
        </span>
        <i className={`bi bi-chevron-${open ? 'up' : 'down'} flex-shrink-0`} aria-hidden='true' />
      </button>
      {open && createPortal(
        <div id='aside-project-tree' ref={popup} role='dialog' aria-label='Select projects'
          className='bg-white border shadow overflow-auto' style={{position: 'fixed', zIndex: 1060, left: position.left, top: position.top, width: position.width, maxHeight: position.height, borderRadius: 8}}>
          {error && <div className='d-flex align-items-center justify-content-between gap-3 px-3 pt-3 text-danger' role='alert'>
            {error}<button className='btn btn-sm btn-icon btn-light' type='button' title='Retry loading projects' aria-label='Retry loading projects' onClick={refresh}><i className='bi bi-arrow-clockwise' aria-hidden='true' /></button>
          </div>}
          <ProjectSelectionTree options={options} value={selectedOptions} onChange={selectProjects} loading={loading}
            isDisabled={Boolean(error)} framed={false} inputId='aside-project-search' labelledBy='aside-project-selector-label'
            maxHeight={Math.max(100, position.height - 85)} />
        </div>, document.body
      )}
    </div>
  )
}

export default ProjectSelector
